import React, { useState, useRef } from 'react';
import { Button, Modal, Form, Input, Select, notification, Tooltip, Icon, Upload, Tag, Alert, message } from 'antd';
import api from "../../services/api";
import { withRouter } from 'react-router-dom';
import isAuthenticated from '../../services/auth';

const { Option } = Select;
const { TextArea } = Input;
const { Dragger } = Upload;

const FeedbackButton = ({ form }) => {
    const [visible, setVisible] = useState(false);
    const [loading, setLoading] = useState(false);
    const [fileList, setFileList] = useState([]);
    const [recording, setRecording] = useState(false);

    const mediaRecorderRef = useRef(null);
    const chunksRef = useRef([]);

    // Check authentication
    if (!isAuthenticated()) return null;

    const showModal = () => {
        setVisible(true);
    };

    const handleCancel = () => {
        if (!loading) {
            setVisible(false);
            setFileList([]);
            form.resetFields();
        }
    };

    const beforeUpload = (file) => {
        setFileList(prev => [...prev, file]);
        return false; // Prevent automatic upload
    };

    const onRemoveFile = (file) => {
        setFileList(prev => prev.filter(f => f.uid !== file.uid));
    };

    const startScreenRecording = async () => {
        if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
            message.error("Seu navegador não suporta gravação de tela.");
            return;
        }

        try {
            const stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
            mediaRecorderRef.current = new MediaRecorder(stream);
            chunksRef.current = [];

            mediaRecorderRef.current.ondataavailable = (e) => {
                if (e.data.size > 0) {
                    chunksRef.current.push(e.data);
                }
            };

            mediaRecorderRef.current.onstop = () => {
                const blob = new Blob(chunksRef.current, { type: 'video/webm' });
                const file = new File([blob], `gravacao-tela-${Date.now()}.webm`, { type: 'video/webm' });
                file.uid = `rc-${Date.now()}`;

                setFileList(prev => [...prev, file]);

                stream.getTracks().forEach(track => track.stop());
                setRecording(false);
                notification.success({ message: 'Gravação de tela anexada!' });
            };

            mediaRecorderRef.current.start();
            setRecording(true);
        } catch (err) {
            console.error("Error recording screen:", err);
        }
    };

    const stopScreenRecording = () => {
        if (mediaRecorderRef.current && recording) {
            mediaRecorderRef.current.stop();
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        form.validateFields(async (err, values) => {
            if (!err) {
                setLoading(true);
                try {
                    const formData = new FormData();
                    formData.append("title", values.title);
                    formData.append("description", values.description || "");
                    formData.append("priority", values.priority);

                    if (fileList.length > 0) {
                        fileList.forEach((file) => {
                            formData.append("attachments", file.originFileObj || file);
                        });
                    }

                    await api.post("/acordo/clickup/tasks/create/", formData, {
                        headers: { "Content-Type": "multipart/form-data" },
                    });

                    notification.success({
                        message: 'Feedback enviado!',
                        description: (
                            <span>
                                Agradecemos sua colaboração! A tarefa foi criada e adicionada ao nosso sistema de acompanhamento.
                            </span>
                        )
                    });
                    setVisible(false);
                    setFileList([]);
                    form.resetFields();

                } catch (error) {
                    console.error('ClickUp Error:', error);
                    const detail = (error && error.response && error.response.data && error.response.data.error) || null;
                    notification.error({
                        message: 'Erro ao enviar',
                        description: detail || 'Não foi possível enviar o feedback. Tente novamente mais tarde.'
                    });
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const { getFieldDecorator, getFieldValue } = form;
    const currentPriority = getFieldValue('priority');

    return (
        <div>
            <div style={{ position: 'fixed', bottom: 20, right: 20, zIndex: 1000 }}>
                <Tooltip title="Sugestões ou Problemas?" placement="left">
                    <Button
                        type="primary"
                        shape="circle"
                        icon="message"
                        size="large"
                        onClick={showModal}
                        style={{ width: 60, height: 60, boxShadow: '0 4px 12px rgba(0,0,0,0.2)', fontSize: '24px' }}
                    />
                </Tooltip>
            </div>

            <Modal
                title="Enviar Sugestão ou Reportar Problema"
                visible={visible}
                onCancel={handleCancel}
                width={600}
                footer={[
                    <Button key="back" onClick={handleCancel} disabled={loading}>
                        Cancelar
                    </Button>,
                    <Button key="submit" type="primary" loading={loading} onClick={handleSubmit}>
                        Enviar
                    </Button>,
                ]}
            >
                <Alert
                    type="info"
                    showIcon
                    message="Importante: verificação antes do envio"
                    description={
                        <div>
                            <div style={{ marginBottom: 8 }}>
                                Antes de enviar, por favor confirme se o problema ocorre de fato (e
                                se não é um comportamento esperado do fluxo). O seu reporte será
                                encaminhado para um board de verificação/triagem e, se aprovado,
                                seguirá para execução.
                            </div>
                            <div>
                                Para acelerar a análise, inclua: passos para reproduzir, resultado
                                esperado vs. atual, e prints/vídeo quando possível.
                            </div>
                        </div>
                    }
                    style={{ marginBottom: 20 }}
                />

                <Form layout="vertical">
                    <Form.Item label="Título *" style={{ marginBottom: 12 }}>
                        {getFieldDecorator('title', {
                            rules: [{ required: true, message: 'Por favor insira um título!' }],
                        })(
                            <Input placeholder="Resumo do problema ou sugestão" />
                        )}
                    </Form.Item>

                    <Form.Item label="Tipo / Prioridade" style={{ marginBottom: 12 }}>
                        {getFieldDecorator('priority', {
                            initialValue: 3,
                            rules: [{ required: true }],
                        })(
                            <Select style={{ width: '100%' }}>
                                <Option value={4}><Tag color="blue">Sugestão / Melhoria</Tag></Option>
                                <Option value={3}><Tag color="orange">Problema (Normal)</Tag></Option>
                                <Option value={1}><Tag color="red">Problema Crítico (Emergência)</Tag></Option>
                            </Select>
                        )}
                        {currentPriority === 1 && (
                            <Alert
                                message="Atenção: Isso notificará a equipe de emergência imediatamente."
                                type="warning"
                                showIcon
                                style={{ marginTop: 10 }}
                            />
                        )}
                    </Form.Item>

                    <Form.Item label="Descrição Detalhada *" style={{ marginBottom: 12 }}>
                        {getFieldDecorator('description', {
                            rules: [{ required: true, message: 'Por favor descreva os detalhes!' }],
                        })(
                            <TextArea rows={4} placeholder="Descreva detalhadamente o que aconteceu ou sua ideia..." />
                        )}
                    </Form.Item>

                    <Form.Item label="Anexos (Prints, Vídeos)">
                        <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                            <Button
                                icon={recording ? "stop" : "video-camera"}
                                onClick={recording ? stopScreenRecording : startScreenRecording}
                                type={recording ? 'danger' : 'default'}
                            >
                                {recording ? 'Parar Gravação' : 'Gravar Tela'}
                            </Button>
                        </div>

                        <Dragger
                            fileList={fileList}
                            beforeUpload={beforeUpload}
                            onRemove={onRemoveFile}
                            multiple
                        >
                            <p className="ant-upload-drag-icon">
                                <Icon type="inbox" />
                            </p>
                            <p className="ant-upload-text">Clique ou arraste arquivos aqui</p>
                            <p className="ant-upload-hint">
                                Suporta imagens e vídeos.
                            </p>
                        </Dragger>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default withRouter(Form.create({ name: 'feedback_form' })(FeedbackButton));



// WEBPACK FOOTER //
// ./src/components/FeedbackButton/index.js